import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { WhaleTracker } from "@/components/social/WhaleTracker";
import { Fish, Bell, Filter } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useWallet } from "@/contexts/WalletContext";
import { Link } from "react-router-dom";

export default function Whales() {
  const { connected, connect } = useWallet();

  return (
    <>
      <Helmet>
        <title>Whale Tracker - TheMoonRun</title>
        <meta name="description" content="Track whale movements on Solana. Get alerts on large trades and follow smart money." />
      </Helmet>
      
      <PageLayout>
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
              <Fish className="w-7 h-7 text-purple-500" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold text-foreground">Whale Tracker</h1>
              <p className="text-muted-foreground">Follow smart money and large trades</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge variant="secondary" className="text-xs">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse mr-2" />
              Live
            </Badge>
            <Button variant="outline" size="sm">
              <Bell className="w-4 h-4" />
              Set Alerts
            </Button>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[
            { label: "Whale Size", value: "≥1 SOL", icon: "🐬", desc: "Trades tracked" },
            { label: "Big Whale", value: "≥10 SOL", icon: "🦈", desc: "Medium trades" },
            { label: "Mega Whale", value: "≥50 SOL", icon: "🐋", desc: "Large trades" },
            { label: "Real-time", value: "5s", icon: "⚡", desc: "Update interval" },
          ].map((stat, i) => (
            <div key={i} className="p-3 sm:p-4 rounded-xl bg-card border border-border">
              <div className="text-2xl mb-2">{stat.icon}</div>
              <p className="text-sm font-semibold text-foreground">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.desc}</p>
            </div>
          ))}
        </div>

        {/* Copy Trading CTA */}
        {connected && (
          <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <h3 className="font-display font-semibold text-foreground mb-1">Auto-Copy Whales</h3>
                <p className="text-sm text-muted-foreground">
                  Set up copy trading to automatically mirror whale trades
                </p>
              </div>
              <Button variant="hero" asChild>
                <Link to="/copy-trading">
                  Set Up Copy Trading
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Connect Wallet CTA */}
        {!connected && (
          <div className="p-4 rounded-xl bg-card border border-border mb-6 text-center">
            <Fish className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-foreground font-medium mb-2">Connect to Track Whales</p>
            <p className="text-sm text-muted-foreground mb-4">
              Connect your wallet to set alerts and copy whale trades
            </p>
            <Button variant="hero" onClick={connect}>Connect Wallet</Button>
          </div>
        )}

        <WhaleTracker />
      </PageLayout>
    </>
  );
}
